<template>
  <div>
    <div class="text-right">
      <button type="button" class="btn btn-primary" @click="$refs.recipientForm.show()">Add Recipient</button>
    </div>

    <top-table
      v-if="!(userState || {}).create"
      ref="table"
      :order-by="{ column: 'createdAt', ascending: 0 }"
      :headings="{ createdAt: 'Created', inActive: 'InActive', generated: 'IsGenerated' }"
      :tab-filters="tabFilters"
      :sortable="['message', 'recipient', 'type']"
      :hasClearFilters="true"
      :columns="['message', 'recipient', 'type', 'description', 'actions']"
      :requestFunction="requestFunction"
      url="/notifications/recipients"
      @loaded="$emit('recipientsLoaded', [...$event.data])"
      :overlap="true"
    >
      <template slot="actions" slot-scope="props">
        <div class="d-flex" :key="props.row._id">
          <a @click="openRecipientForm(props.row)" class="mr-2">
            <i class="far fa-edit"></i>
          </a>
          <a @click="deleteRecipientConfirmation(props.row)">
            <i class="far fa-trash"></i>
          </a>
        </div>
      </template>
    </top-table>
    <b-modal ref="recipientForm" @hide="closeModal" hide-footer hide-header>
      <div class="d-flex justify-content-between align-items-center">
        <h3>Add Notification </h3>
      </div>
      <div>
        <b-form-group label="Type">
          <b-form-select v-model="currentRecipient.type" :options="recipientTypesSelect"/>
          <span class="invalid-feedback d-block" v-if="$v.currentRecipient.type.$error">Notification type is required</span>
        </b-form-group>
        <b-form-group class="mb-0">
          <slot name="label">
            <label>Recipient</label>
            <i 
              v-if="currentRecipient.type === 'Slack'"
              class="fas fa-question-circle text-light ml-2"
              v-b-popover.hover="{
                title: 'Recipient',
                content: 'Please note for Slack messages to private channels you will need to add the MPower Bot to the Slack channel. This can be found under #yourchannel > Info > More > Add Apps.'
              }"
            ></i>
          </slot>
          <div v-if="currentRecipient.type === 'Slack'" class="mb-3">
            <multiselect
              placeholder="Select Option..."
              :custom-label="slackRecipientsListLabel"
              :options="slackRecipientsList"
              track-by="id"
              v-model="slackRecipient"
            />
            <span class="invalid-feedback d-block" v-if="$v.slackRecipient?.$error">Slack Recipient is required</span>
          </div>
          <div v-else-if="currentRecipient.type === 'Email'" >
            <validated-input v-model="currentRecipient.recipient" />
            <div v-if="$v.currentRecipient.recipient?.$error">
              <span class="invalid-feedback d-block extra-space" v-if="$v.currentRecipient.recipient?.required && $v.currentRecipient.recipient?.$invalid">Email value is invalid</span>
              <span class="invalid-feedback d-block extra-space" v-if="!$v.currentRecipient.recipient?.required">Email field is required</span>
            </div>
          </div>
          <div v-else>
            <validated-input v-model="currentRecipient.recipient"/>
            <span class="invalid-feedback d-block extra-space" v-if="$v.currentRecipient.recipient?.$error">Recipient field is required</span>
          </div>
        </b-form-group>
        
        <validated-input v-model="currentRecipient.description" label="Description" :validate="{ required: true }" />
        <validated-input
          v-model="currentRecipient.message"
          :type="'text-area'"
          label="Message"
          :validate="{ required: true }"
        />

        <div class="pt-3 small">
          <div class="alert alert-info">
            <h6 class="font-weight-normal">Allowed list of dynamic variables in message:</h6>
            <b>{{
              [
                '${orderId}',
                '${orderTotal}',
                '${paymentTotal}',
                '${linkToOrder}',
                '${notificationType}',
                '${notificationCategory}',
                '${notificationName}',
                '${salesUserName}',
                '${adminUserName}',
                '${createdFollowUpUserName}',
                '${customerName}',
                '${storeName}',
                '${followUpNotes}',
                '${followUpDate}',
                '${followUpType}',
                '${friendlyPurchaseOrderDetailId}',
                '${purchaseOrderStatus}',
                '${customerCompany}',
                isSuperAdmin && '${details}',
                isSuperAdmin && '${distributorName}',
                isSuperAdmin && '${distributorPlan}'
              ]
                .filter((e) => e)
                .join(', ')
            }}</b>
          </div>
        </div>
        <div class="form-inline mt-3">
          <button type="button" :disabled="pending" class="btn btn-primary" @click="saveRecipient">
            <i v-if="pending" class="far fa-spinner-third fa-spin mr-2"></i>Save
          </button>
          <a class="ml-3" @click="closeModal">Cancel</a>
        </div>
      </div>
    </b-modal>
    <b-modal ref="deleteRecipientConfirm" title="Delete Notification" @ok="deleteRecipient">
      <p v-if="recipientToDelete">
        Are you sure you want to remove recipient
        <b>{{ recipientToDelete.recipient }}</b> in <b>{{ recipientToDelete.type }}</b
        >?
      </p>
    </b-modal>
  </div>
</template>

<script>
import {
  createNotificationRecipient,
  updateNotificationRecipient,
  deleteNotificationRecipient
} from '@/api/notifications';
import { validationMixin } from 'vuelidate';
import { required, email, minLength, maxLength } from 'vuelidate/lib/validators';

export default {
  data() {
    let recipientTypes = ['Slack', 'Email', 'Phone'];
    return {
      pending: false,
      recipientToDelete: null,
      slackRecipient: null,
      currentRecipient: {
        message:
          '${notificationType} - ${orderId}-${friendlyPurchaseOrderDetailId} ${customerName} (${customerCompany}) $${orderTotal} ${linkToOrder}'
      },
      recipientTypes,
      tabFilters: [
        {
          label: 'Type',
          val: 'type',
          criteria: recipientTypes.map((i) => ({ label: i }))
        }
      ]
    };
  },
  mixins: [validationMixin],
  validations() {
    const validation = {
      currentRecipient: {
        type: { required }
      }
    }
    if (this.currentRecipient.type === 'Email'){
      validation.currentRecipient.recipient = { required, email }
    } 
    if (this.currentRecipient.type === 'Slack'){
      validation.slackRecipient = { required }
    }
    if (this.currentRecipient.type === 'Phone'){
      validation.currentRecipient.recipient = { 
        required,
        minLength: minLength(8),
        maxLength: maxLength(12)
      }
    }
    return validation;
  },
  inject: ['requestParams'],
  props: {
    slackRecipientsList: {
      type: Array,
      default: () => []
    },
    slackConnected: Boolean,
    userState: Object
  },
  watch: {
    slackRecipient: {
      handler(val) {
        if (val) {
          this.currentRecipient.recipient = val.id;
          if (!this.currentRecipient.description) {
            this.currentRecipient.description = val.real_name || `#${val.name}`;
          }
        }
      },
      deep: true
    }
  },
  computed: {
    recipientTypesSelect() {
      return this.recipientTypes.map((i) => ({
        text: i,
        value: i,
        disabled: i === 'Slack' && !this.slackConnected
      }));
    }
  },
  methods: {
    closeModal() {
      this.$v.$reset();
      this.currentRecipient = { message: this.currentRecipient.message };
      this.slackRecipient = null;
      this.$refs.recipientForm.hide();
    },
    slackRecipientsListLabel(val) {
      if (val.is_channel || val.is_private) {
        return `#${val.name}`;
      }
      return val.real_name;
    },
    clearTable() {
      this.$refs.table.refresh();
      this.$emit('refresh');
    },
    requestFunction(p) {
      p.pagination = true;
      Object.assign(p, this.requestParams);
    },
    async saveRecipient() {
      this.$v.$touch();
      if (this.$v.$anyError) {
        return;
      }
      try {
        this.pending = true;
        Object.assign(this.currentRecipient, this.requestParams);
        if (this.currentRecipient._id) {
          await updateNotificationRecipient(this.currentRecipient);
        } else {
          await createNotificationRecipient(this.currentRecipient);
        }
        this.clearTable();
        this.closeModal();
      } catch (e) {
        console.log(e);
      } finally {
        this.pending = false;
        this.$v.$reset();
      }
    },
    async deleteRecipient() {
      await deleteNotificationRecipient(this.recipientToDelete._id);
      this.clearTable();
    },
    openRecipientForm(data) {
      this.currentRecipient = Object.assign({}, data);
      if (this.currentRecipient.type === 'Slack' && this.currentRecipient.recipient) {
        this.slackRecipient = this.slackRecipientsList.find((i) => i.id === this.currentRecipient.recipient);
      }
      this.$refs.recipientForm.show();
    },
    deleteRecipientConfirmation(data) {
      this.recipientToDelete = data;
      this.$refs.deleteRecipientConfirm.show();
    }
  },
};
</script>
<style scoped>
.extra-space{
  margin: -14px 0 10px !important;
}
</style>
